'use client'

import React from 'react'
import Button from '@/components/ui/Button'
import { useCookieConsent } from '@/context/cookie-consent-context/CookieConsentContext'
import { Settings, Cookie, Shield, Check, X } from 'lucide-react'
import Link from 'next/link'
import Image from 'next/image'

export function CookieSettingsButton() {
  const { openPreferences } = useCookieConsent()

  return (
    <Button 
      onClick={openPreferences}
      variant="plain"
      size="default"
      className="flex items-center gap-2 px-4 py-2 text-gray-700 border-gray-300 hover:border-gray-400 hover:text-gray-800"
    >
      <Settings className="w-4 h-4" />
      Cookie Settings
    </Button>
  )
}

// Text link variant for footers
export function CookieSettingsFooterLink() {
  const { openPreferences } = useCookieConsent()

  return (
    <button
      onClick={openPreferences}
      className="text-sm text-gray-600 hover:text-gray-800 underline transition-colors"
    >
      Cookie Settings
    </button>
  )
}

// Icon-only variant for compact spaces
export function CookieSettingsIcon() {
  const { openPreferences } = useCookieConsent()

  return (
    <button
      onClick={openPreferences}
      className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
      aria-label="Cookie Settings"
      title="Cookie Settings"
    >
      <div className="w-5 h-5">
        <Image
          src="/images/cookie-icon.svg"
          alt="Cookie Settings"
          width={20}
          height={20}
          className="w-full h-full object-contain"
        />
      </div>
    </button>
  )
}

// Card showing current preferences
export function CookieSettingsCard() {
  const { consent, openPreferences } = useCookieConsent()

  if (!consent) return null

  const enabledCount = Object.values(consent).filter(Boolean).length
  const totalCount = Object.keys(consent).length

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
            <div className="w-6 h-6">
              <Image
                src="/images/cookie-icon.svg"
                alt="Cookie"
                width={24}
                height={24}
                className="w-full h-full object-contain"
              />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-gray-900 mb-1">Cookie Preferences</h3>
            <p className="text-sm text-gray-600 mb-3">
              {enabledCount} of {totalCount} cookie categories enabled
            </p>
            
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs">
                {consent.essential ? (
                  <Check className="w-3 h-3 text-green-600" />
                ) : (
                  <X className="w-3 h-3 text-red-600" />
                )}
                <span className={consent.essential ? 'text-green-700' : 'text-red-700'}>
                  Essential
                </span>
              </div>
              
              <div className="flex items-center gap-2 text-xs">
                {consent.analytics ? (
                  <Check className="w-3 h-3 text-green-600" />
                ) : (
                  <X className="w-3 h-3 text-red-600" />
                )}
                <span className={consent.analytics ? 'text-green-700' : 'text-red-700'}>
                  Analytics
                </span>
              </div>
              
              <div className="flex items-center gap-2 text-xs">
                {consent.marketing ? (
                  <Check className="w-3 h-3 text-green-600" />
                ) : (
                  <X className="w-3 h-3 text-red-600" />
                )}
                <span className={consent.marketing ? 'text-green-700' : 'text-red-700'}>
                  Marketing
                </span>
              </div>
              
              {/* <div className="flex items-center gap-2 text-xs">
                {consent.preferences ? (
                  <Check className="w-3 h-3 text-green-600" />
                ) : (
                  <X className="w-3 h-3 text-red-600" />
                )}
                <span className={consent.preferences ? 'text-green-700' : 'text-red-700'}>
                  Preferences
                </span>
              </div> */}
            </div>
          </div>
        </div>
        
        <Button 
          onClick={openPreferences}
          variant="plain"
          size="default"
          className="flex-shrink-0 px-3 py-1.5 text-sm text-gray-700 border-gray-300 hover:border-gray-400"
        >
          <Settings className="w-4 h-4 mr-1" />
          Edit
        </Button>
      </div>
    </div>
  )
} 